"use client"

import { Fragment, useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"
import clsx from "clsx"

import type { AlgoliaProductHit, AlgoliaPromoCardHit } from "~/lib/algolia/types"
import { useAlgoliaSearch } from "~/hooks/useAlgoliaSearch"
import {
  isProductCardHit,
  isPromoCardHit,
} from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/_utils/check-hit-type"
import { AlgoliaCardDispatcher } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/AlgoliaCardDispatcher/index.client"
import { AlgoliaGridContent } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/AlgoliaGridContent/index.client"
import { GridLayout } from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/GridLayout/index.client"
import {
  AlgoliaListingGrid,
  type AlgoliaListingGridProps,
} from "~/components/ui/AlgoliaCollectionGrid/AlgoliaListingGrid/index.client"
import type { ImageProps } from "~/components/ui/Image"
import type { RhythmItems } from "~/providers/CardImagesRhythmProvider"
import { AlgoliaClientViewItemList } from "~/providers/GTMTrackingProvider/ClientViewItemList"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"

import * as css from "./styles.css"

type AlgoliaHitsProps = Omit<AlgoliaListingGridProps, "mixedHits"> & {
  collectionCardRhythm?: RhythmItems
  collectionIsEmpty?: boolean
} & Pick<ImageProps, "priority">

function AlgoliaHits({
  hits,
  collectionCardRhythm,
  collectionIsEmpty = false,
  priority,
}: {
  hits: (AlgoliaProductHit | AlgoliaPromoCardHit)[]
} & Pick<AlgoliaHitsProps, "collectionCardRhythm" | "priority" | "collectionIsEmpty">) {
  const t = useTranslate()

  // This index is used to keep track of the Algolia hits
  let algoliaCardsIndex = 0

  // Getting the results from the Algolia search
  const hasResults = hits?.length > 0

  return (
    <Fragment>
      {!hasResults && !collectionIsEmpty && <p className={css.noResults}>{t("search_no_result")}</p>}
      {hits?.filter(Boolean)?.map((hit, index) => {
        const isProductCard = isProductCardHit(hit)
        const isPromoCard = isPromoCardHit(hit)

        // If the item is an Algolia hit, increment the index
        if (isProductCard) {
          algoliaCardsIndex++
        }

        // We need to get the firstImageType (either packshot, american, or worn) in order to get some card images rhythm
        const firstImageType = collectionCardRhythm?.[algoliaCardsIndex % collectionCardRhythm?.length]

        return (
          <div
            key={`algolia-hit-${hit?.objectID}-${index}`}
            className={clsx(isPromoCard && css.layoutCard[isProductCard ? "product" : hit.promoCard.layout])}
          >
            <AlgoliaCardDispatcher firstImageType={firstImageType} priority={priority && index === 0} {...hit} />
          </div>
        )
      })}
    </Fragment>
  )
}

export function AlgoliaCollectionGrid({
  collectionName,
  collectionHandle,
  collectionNavigationLinks,
  collectionCardRhythm,
  priority,
  collectionIsEmpty,
}: AlgoliaHitsProps) {
  const router = useRouter()
  const pathname = usePathname()
  const { data } = useAlgoliaSearch()
  const results = data?.hits ?? []

  useEffect(() => {
    router.prefetch(`${pathname}/1`)
  }, [])

  return (
    <AlgoliaListingGrid
      collectionNavigationLinks={collectionNavigationLinks}
      collectionName={collectionName}
      isEmpty={collectionIsEmpty}
    >
      <AlgoliaGridContent>
        <AlgoliaHits
          priority={priority}
          hits={results}
          collectionCardRhythm={collectionCardRhythm}
          collectionIsEmpty={collectionIsEmpty}
        />

        <AlgoliaClientViewItemList
          results={results}
          collectionProps={{
            title: collectionName || "",
            handle: collectionHandle || "",
          }}
        />
      </AlgoliaGridContent>
    </AlgoliaListingGrid>
  )
}

export function SuspensedAlgoliaCollectionGrid({
  hits,
  collectionName,
  collectionNavigationLinks,
  collectionCardRhythm,
  priority,
  collectionIsEmpty,
}: { hits: (AlgoliaProductHit | AlgoliaPromoCardHit)[] } & AlgoliaHitsProps) {
  return (
    <AlgoliaListingGrid
      collectionNavigationLinks={collectionNavigationLinks}
      collectionName={collectionName}
      isEmpty={collectionIsEmpty}
    >
      <GridLayout>
        <AlgoliaHits
          priority={priority}
          hits={hits}
          collectionCardRhythm={collectionCardRhythm}
          collectionIsEmpty={collectionIsEmpty}
        />
      </GridLayout>
    </AlgoliaListingGrid>
  )
}
